import React, { useEffect, useState } from 'react';
import { Mail, Phone, Send, ArrowRight, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal from '../components/layout/ScrollReveal';
import { submitContact } from '../lib/api';
import { toast } from 'sonner';

const INQUIRY_TYPES = [
  'Partnership Inquiry',
  'Technology Consultation',
  'Infrastructure Assessment',
  'Product Development',
  'SaaS Platform Build',
  'Digital Transformation',
  'API Integration',
  'Cloud Migration',
  'Performance Optimization',
  'Security Audit',
  'Custom Development',
  'Ecosystem Integration',
  'Scaling & Growth Strategy',
  'White Label Solution',
  'Investment Opportunity',
  'Media & Press Inquiry',
  'Careers & Talent',
  'General Inquiry',
];

const BUDGET_RANGES = [
  'Under $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  '$250,000+',
  'Not specified',
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'Partnership Inquiry',
    message: '',
    budget: 'Not specified',
    honeypot: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = 'Contact';
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);
    try {
      await submitContact(formData);
      setSent(true);
      toast.success('Inquiry submitted successfully. We will be in touch.');
    } catch {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <PageTransition>
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 bg-fire-engine" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal hero>
            <div className="flex items-center gap-2 mb-5">
              <Flame className="h-4 w-4 text-[#E8722A]" />
              <span className="kicker">Contact</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">
              Discuss business
            </h1>
            <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-xl">
              Ready to explore how Visualte can power your next digital venture?
              Submit your inquiry and our team will respond within 24 hours.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-12 sm:py-16 relative">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-10">
            <ScrollReveal hero>
              {sent ? (
                <div className="hud-card border border-[rgba(232,114,42,0.25)] bg-[rgba(232,114,42,0.04)] p-8 text-center" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-[rgba(232,114,42,0.1)] border border-[rgba(232,114,42,0.2)] flex items-center justify-center mx-auto mb-5">
                    <Send className="h-6 w-6 text-[#E8722A]" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Inquiry Received</h3>
                  <p className="text-sm text-white/55">Thank you. Our team will review your inquiry and respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} className="hidden" tabIndex={-1} autoComplete="off" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-white/55 mb-1.5 block">Full Name *</label>
                      <Input data-testid="contact-name-input" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E8722A] focus-visible:ring-offset-2 focus-visible:ring-offset-black" />
                    </div>
                    <div>
                      <label className="text-xs text-white/55 mb-1.5 block">Work Email *</label>
                      <Input data-testid="contact-email-input" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" required className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E8722A] focus-visible:ring-offset-2 focus-visible:ring-offset-black" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-white/55 mb-1.5 block">Company</label>
                    <Input data-testid="contact-company-input" name="company" value={formData.company} onChange={handleChange} placeholder="Your company name" className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E8722A] focus-visible:ring-offset-2 focus-visible:ring-offset-black" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-white/55 mb-1.5 block">Inquiry Type *</label>
                      <select data-testid="contact-subject-select" name="subject" value={formData.subject} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E8722A] focus:ring-offset-2 focus:ring-offset-black">
                        {INQUIRY_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-[#0A0F15] text-white">{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-white/55 mb-1.5 block">Budget Range</label>
                      <select data-testid="contact-budget-select" name="budget" value={formData.budget} onChange={handleChange} className="w-full h-10 px-3 rounded-md border border-white/10 bg-black/40 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E8722A] focus:ring-offset-2 focus:ring-offset-black">
                        {BUDGET_RANGES.map((b) => (
                          <option key={b} value={b} className="bg-[#0A0F15] text-white">{b}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-white/55 mb-1.5 block">Message *</label>
                    <textarea data-testid="contact-message-textarea" name="message" value={formData.message} onChange={handleChange} placeholder="Describe your project or inquiry..." required rows={5} className="w-full px-3 py-2 rounded-md border border-white/10 bg-black/40 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8722A] focus:ring-offset-2 focus:ring-offset-black resize-none" />
                  </div>

                  <Button type="submit" disabled={sending} data-testid="contact-form-submit-button" className="btn-primary-gradient btn-shimmer font-semibold h-12 px-6 text-base w-full sm:w-auto rounded-lg disabled:opacity-50">
                    {sending ? 'Sending...' : 'Submit Inquiry'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              )}
            </ScrollReveal>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <ScrollReveal delay={0.2} direction="right" distance={20}>
                <div className="hud-card border border-white/10 bg-white/[0.03] p-6">
                  <span className="kicker block mb-4">Direct Channel</span>
                  <div className="space-y-4">
                    <ContactItem icon={Mail} label="General Inquiries" value="info@visualte.com" href="mailto:info@visualte.com" />
                    <ContactItem icon={Phone} label="Phone" value="+972 50 237 2931" href="tel:+972502372931" />
                  </div>

                  <Separator className="my-5 bg-white/8" />

                  <div className="text-xs text-white/40">
                    <p>We typically respond within 24 hours on business days.</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}

function ContactItem({ icon: Icon, label, value, href }) {
  return (
    <a href={href} className="flex items-center gap-3 p-3 rounded-lg border border-white/6 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 transition-all duration-200 group">
      <div className="w-8 h-8 rounded-md bg-[rgba(232,114,42,0.08)] border border-[rgba(232,114,42,0.15)] flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#E8722A]" />
      </div>
      <div>
        <div className="text-xs text-white/40">{label}</div>
        <div className="text-sm text-white/80 group-hover:text-[#E8722A] transition-colors">{value}</div>
      </div>
    </a>
  );
}
